import { Button, Form, Space, Steps } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/store";
import ArrowLeftIcon from "../../../assets/icons/ArrowLeftIcon";
import ArrowRightIcon from "../../../assets/icons/ArrowRightIcon";
import CheckCircleIcon from "../../../assets/icons/CheckCircleIcon";
import TitleBar from "../../../components/common/TitleBar";
import ConfirmModal from "../../../components/ui/ConfirmModal";
import WithLoader from "../../../components/WithLoader";
import {
  useCreateVendorMutation,
  useGetVendorDetailsQuery,
  useUpdateVendorMutation,
} from "../../../features/Vendor/vendor.api";
import {
  setVendor,
  updateContacts,
  updateDocuments,
  updateGeneralInfo,
  updatePayments,
} from "../../../features/Vendor/vendor.slice";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { toast } from "../../../lib/toast";
import { cn } from "../../../utils/string";
import Contacts from "./_components/ManageVendor/Contacts";
import Documents from "./_components/ManageVendor/Documents";
import General from "./_components/ManageVendor/General";
import Payments from "./_components/ManageVendor/Payments";
const { Step } = Steps;

/**
 * TODO:
 * Bank delete
 */

const ManageVendor = () => {
  const { id } = useParams();
  const isUpdating = !!id;
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { open, handleOpen, handleClose } = useModal();

  const { vendorDate } = useAppSelector((state) => state.vendor);

  // ------ Component States ------
  const [current, setCurrent] = useState<number>(
    Number(localStorage.getItem("vendor-step")) || 0,
  );

  // ------ Queries ------
  const { data: vendorResponse, isLoading } = useGetVendorDetailsQuery(
    Number(id) as number,
    { skip: !id },
  );

  // ------ Mutations ------
  const [
    createVendor,
    {
      isLoading: creating,
      isSuccess: created,
      isError: isCreateError,
      error: createError,
    },
  ] = useCreateVendorMutation();
  const [
    updateVendor,
    {
      isLoading: updating,
      isSuccess: updated,
      isError: isUpdateError,
      error: updateError,
    },
  ] = useUpdateVendorMutation();

  // ------ Function for go to the next step with form validation ------
  const handleNavigateNext = async () => {
    try {
      const values = await form.validateFields();

      localStorage.setItem(
        "vendor-data",
        JSON.stringify({ ...vendorDate, ...values }),
      );
      localStorage.setItem("vendor-step", JSON.stringify(current + 1));

      if (current === 0) {
        let result;
        if (isUpdating) {
          result = await updateVendor({
            data: values,
            id: vendorDate?.id,
          }).unwrap();
        } else {
          result = await createVendor({
            ...values,
          }).unwrap();
        }
        dispatch(
          updateGeneralInfo({ ...values, id: result?.data?.vendor?.id }),
        );
      } else if (current === 1) {
        updateVendor({ data: values, id: vendorDate?.id });
        dispatch(updateContacts({ ...values }));
      } else if (current === 2) {
        updateVendor({ data: values, id: vendorDate?.id });
        dispatch(updatePayments({ ...values }));
      } else if (current === 3) {
        // updateVendor({ data: values, id: vendorDate?.id });
        dispatch(updateDocuments({ ...values }));
        handleOpen({ type: "VIEW", data: { ...values, status: "active" } });
        localStorage.removeItem("vendor-data");
        localStorage.removeItem("vendor-step");
      }
    } catch (err) {}
  };

  // Function for got to prev step
  const handleNavigatePrev = () => {
    setCurrent((prev) => (prev === 0 ? 0 : prev - 1));
  };

  useEffect(() => {
    if ((created || updated) && !(creating || updating)) {
      setCurrent((prev) => (prev === 3 ? 3 : prev + 1));
      if (current === 3) {
        navigate("/admin/vendors");
        toast(
          "success",
          isUpdating
            ? "Vendor created successfully."
            : "Vendor updated successfully.",
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [created, creating, updated, updating]);

  useEffect(() => {
    if (vendorResponse?.data) {
      form.setFieldsValue({
        ...vendorResponse.data,
        paymentInfo: vendorResponse.data.payments,
      });
      dispatch(setVendor(vendorResponse.data));
    }
  }, [vendorResponse, form, dispatch]);

  useEffect(() => {
    const storedData = localStorage.getItem("vendor-date");
    if (storedData) {
      const parsedData = JSON.parse(storedData || "{}");
      dispatch(setVendor(parsedData));
      form.setFieldsValue(parsedData);
    }

    return () => {
      localStorage.removeItem("vendor-data");
      localStorage.removeItem("vendor-step");
    };
  }, [dispatch, form]);

  // ------ Errors ------
  useError(isCreateError, createError, "Vendor create failed.");
  useError(isUpdateError, updateError, "Vendor update failed.");

  // ------ Step contents ------
  const steps = [
    {
      key: "general",
      title: "General",
      content: <General form={form} />,
    },
    {
      key: "contacts",
      title: "Contacts",
      content: <Contacts form={form} />,
    },
    {
      key: "payment",
      title: "Payment",
      content: <Payments form={form} />,
    },
    {
      key: "documents",
      title: "Documents",
      content: <Documents form={form} />,
    },
  ];

  let questionText = "";
  if (id) {
    questionText = `Confirm update: Do you want to change the vendor "${vendorDate?.business_name}"?`;
  } else {
    questionText = `Are you sure you want to add “${vendorDate?.business_name}”? This will set their status to active.`;
  }

  return (
    <SidebarLayout title="Vendor Management">
      <TitleBar
        backLink="/admin/vendors"
        title="Vendor Registration"
        subTitle="Manage vendor registration and profile information"
      />
      <div className="max-w-3xl mx-auto my-9">
        <Steps current={current} style={{ marginBottom: 20 }}>
          {steps.map((item, index) => (
            <Step
              icon={
                <span
                  className={cn(
                    "block mt-[3px]",
                    index < current
                      ? "text-theme-success-500"
                      : index === current
                      ? "text-theme-yellow-600"
                      : "text-theme-gray-40",
                  )}
                >
                  <CheckCircleIcon />
                </span>
              }
              key={item.title}
              title={item.title}
            />
          ))}
        </Steps>
      </div>
      {/* Render body content hare  */}
      <WithLoader isLoading={isLoading}>{steps?.[current]?.content}</WithLoader>

      <Space className="flex items-center justify-between">
        <Button
          onClick={() => navigate("/admin/vendors")}
          className="button-white-bordered"
        >
          Cancel
        </Button>
        <Space>
          <Button
            onClick={handleNavigatePrev}
            className="button-white-bordered"
          >
            <ArrowLeftIcon />
            Previous
          </Button>
          <Button
            loading={(creating || updating) && current !== 3}
            onClick={handleNavigateNext}
            className="button-primary"
          >
            {isUpdating ? "Update" : "Save"} {current !== 3 && "and Continue"}
            {current !== 3 && <ArrowRightIcon />}
          </Button>
        </Space>
      </Space>

      <ConfirmModal
        closable
        icon="WARNING"
        okText={"Yes, Confirm"}
        confirmTitle="Register Vendor"
        questionText={questionText}
        open={open.open}
        okLoading={creating || updating}
        handleClose={handleClose}
        handleOk={() => {
          updateVendor({ data: open.data, id: vendorDate?.id });
        }}
      />
    </SidebarLayout>
  );
};

export default ManageVendor;
